/** EnSo application-list ids that grant access to Business Hub deal registration UI. */
export const DEAL_REGISTRATION_ACCESS_IDS = ["deal_registration", "business_hub"] as const;

export type DealRegistrationAccessId = (typeof DEAL_REGISTRATION_ACCESS_IDS)[number];

export const DEAL_REGISTRATION_ROUTE_SEGMENT = "deal_registration";

type IamMetadata = {
  applicationListNameMapping?: Record<string, string>;
};

/** Role application list includes deal registration (or business_hub alias). */
export function hasDealRegistrationApplicationAccess(
  userAccessInfo: string[] | undefined | null
): boolean {
  if (!Array.isArray(userAccessInfo)) return false;
  return DEAL_REGISTRATION_ACCESS_IDS.some((id) => userAccessInfo.includes(id));
}

type PersonaSource = {
  persona?: string[];
  role?: { persona?: string[] };
};

function normalizePersonaToken(value: string): string {
  return String(value).toLowerCase().replace(/_/g, "-").trim();
}

function personaTokens(source: PersonaSource | null | undefined): string[] {
  if (!source || typeof source !== "object") return [];
  const persona = source.persona ?? source.role?.persona;
  if (!Array.isArray(persona)) return [];
  return persona.map((p) => normalizePersonaToken(String(p)));
}

/** Internal Nokia users may download deal export (FR-009). */
export function hasNokiaInternalPersona(source: PersonaSource | null | undefined): boolean {
  return personaTokens(source).some((p) => p === "nokia");
}

/** Nav + routes: same gate as other apps — application list membership only. */
export function canAccessDealRegistration(
  userAccessInfo: string[] | undefined | null
): boolean {
  return hasDealRegistrationApplicationAccess(userAccessInfo);
}

/** @deprecated Use hasDealRegistrationApplicationAccess */
export function hasDealRegistrationAccess(userAccessInfo: string[] | undefined | null): boolean {
  return hasDealRegistrationApplicationAccess(userAccessInfo);
}

export function ensureDealRegistrationAccessIds(applications: string[]): string[] {
  const next = [...applications];
  if (hasDealRegistrationApplicationAccess(next)) {
    for (const id of DEAL_REGISTRATION_ACCESS_IDS) {
      if (!next.includes(id)) next.push(id);
    }
  }
  return next;
}

/**
 * Keep deal registration on the nav when the role application list already includes it.
 * Does not consult data-element permissions.
 */
export function applyDealRegistrationNavEntitlement(
  applications: string[],
  metadata: IamMetadata | null | undefined
): string[] {
  if (!hasDealRegistrationApplicationAccess(applications)) return applications;

  const next = ensureDealRegistrationAccessIds(applications);
  if (metadata?.applicationListNameMapping && !metadata.applicationListNameMapping.deal_registration) {
    metadata.applicationListNameMapping.deal_registration = "Deal Registration";
  }
  return next;
}
